/**
 * User-facing error messages for provider validation
 */
export const VALIDATION_MESSAGES = Object.freeze({
  DUPLICATE_PROVIDER: 'Provider is already in roster',
  DUPLICATE_EMAIL:
    'This email is already associated with another provider in your roster',
  LAST4_REQUIRED: 'Last 4 characters required',
  LAST4_INVALID: 'Must be exactly 4 alphanumeric characters',
  LAST4_MISMATCH: 'Last 4 digits do not match our records',
  LAST4_VERIFICATION_FAILED: 'Verification failed. Please try again.',
  EMAIL_REQUIRED: 'Email is required',
  EMAIL_INVALID: 'Please enter a valid email',
  EMAIL_NOT_PROFESSIONAL: 'Please use a professional email address',
  EMAIL_TAKEN: 'This email address is already in use',
  EMAIL_VALIDATION_FAILED: 'Email validation failed. Please try again.',
  NPI_NOT_FOUND: 'No provider found with this NPI',
  NPI_INVALID: 'NPI must be 10 digits',
  NAME_NOT_FOUND: 'No provider found with this name',
  SEARCH_ERROR: 'Search failed. Please try again.',
});

/**
 * Error types for form validation.
 * These are used by React Hook Form to categorize validation errors.
 * Keep these simple - users see the message, not the type.
 */
export const ERROR_TYPES = Object.freeze({
  /** User input is invalid (wrong format, not professional, etc.) */
  INVALID_INPUT: 'invalidInput',

  /** Search found no results (provider/email doesn't exist) */
  NOT_FOUND: 'notFound',

  /** Duplicate detected (provider or email already in roster) */
  ALREADY_EXISTS: 'alreadyExists',

  /** System error (API failed, network error, etc.) */
  VALIDATION_ERROR: 'validationError',
});

/**
 * User-facing instructions copy for the "Add Providers to Roster" flow.
 */
export const INSTRUCTIONS_COPY = Object.freeze({
  ADD_PROVIDER: Object.freeze({
    EMPHASIS: 'To add a healthcare professional,',
    BODY: 'search by NPI or name and location to select their profile. Enter the last four digits of their DEA or license number for validation, and provide their work email address (personal domains like Gmail or Yahoo are not accepted).',
  }),

  ONCE_VERIFIED: Object.freeze({
    EMPHASIS: 'Once verified,',
    BODY: 'the professional can be added to your roster for purchase—unless their profile is already sponsored by another entity.',
  }),

  AFTER_PURCHASE: Object.freeze({
    EMPHASIS: 'After purchasing the subscription,',
    BODY_BEFORE_EMAIL:
      'we will notify the healthcare professional that they have been added to your practice subscription, as they may decline it at any time. For assistance regarding your practice subscription, please contact',
    SUPPORT_EMAIL: 'elevatedprofile@healthgrades.com',
    SUPPORT_EMAIL_HREF: 'mailto:elevatedprofile@healthgrades.com',
  }),
});

/**
 * User-facing copy for CSV upload flows.
 */
export const CSV_UPLOAD_MESSAGES = Object.freeze({
  DUPLICATE_PROVIDERS_OVER_10:
    'We identified more than 10 duplicate providers in your CSV. Please check your CSV file and remove duplicates.',
  duplicateProvidersWithRows: (rowListText) =>
    `We identified duplicate providers in your CSV. Please check your CSV file for rows ${rowListText} and remove duplicates.`,
});
